import { useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { MapPin, ArrowRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import maplibregl from 'maplibre-gl';
import 'maplibre-gl/dist/maplibre-gl.css';

const serverLocations = [
  { name: 'Los Angeles', region: 'North America', coords: [-118.2437, 34.0522] },
  { name: 'San Francisco', region: 'North America', coords: [-122.4194, 37.7749] },
  { name: 'Seattle', region: 'North America', coords: [-122.3321, 47.6062] },
  { name: 'Dallas', region: 'North America', coords: [-96.7970, 32.7767] },
  { name: 'Chicago', region: 'North America', coords: [-87.6298, 41.8781] },
  { name: 'Atlanta', region: 'North America', coords: [-84.3880, 33.7490] },
  { name: 'Miami', region: 'North America', coords: [-80.1918, 25.7617] },
  { name: 'New York', region: 'North America', coords: [-74.0060, 40.7128] },
  { name: 'Toronto', region: 'North America', coords: [-79.3832, 43.6532] },
  { name: 'Vancouver', region: 'North America', coords: [-123.1207, 49.2827] },
  { name: 'London', region: 'Europe', coords: [-0.1276, 51.5074] },
  { name: 'Amsterdam', region: 'Europe', coords: [4.9041, 52.3676] },
  { name: 'Frankfurt', region: 'Europe', coords: [8.6821, 50.1109] },
  { name: 'Paris', region: 'Europe', coords: [2.3522, 48.8566] },
  { name: 'Stockholm', region: 'Europe', coords: [18.0686, 59.3293] },
  { name: 'Madrid', region: 'Europe', coords: [-3.7038, 40.4168] },
  { name: 'Milan', region: 'Europe', coords: [9.1900, 45.4642] },
  { name: 'Warsaw', region: 'Europe', coords: [21.0122, 52.2297] },
  { name: 'Tokyo', region: 'Asia Pacific', coords: [139.6917, 35.6895] },
  { name: 'Seoul', region: 'Asia Pacific', coords: [126.9780, 37.5665] },
  { name: 'Singapore', region: 'Asia Pacific', coords: [103.8198, 1.3521] },
  { name: 'Hong Kong', region: 'Asia Pacific', coords: [114.1694, 22.3193] },
  { name: 'Sydney', region: 'Asia Pacific', coords: [151.2093, -33.8688] },
  { name: 'Melbourne', region: 'Asia Pacific', coords: [144.9631, -37.8136] },
  { name: 'Mumbai', region: 'Asia Pacific', coords: [72.8777, 19.0760] },
  { name: 'Bangkok', region: 'Asia Pacific', coords: [100.5018, 13.7563] },
  { name: 'São Paulo', region: 'South America', coords: [-46.6333, -23.5505] },
  { name: 'Buenos Aires', region: 'South America', coords: [-58.3816, -34.6037] },
  { name: 'Santiago', region: 'South America', coords: [-70.6693, -33.4489] },
  { name: 'Dubai', region: 'Middle East & Africa', coords: [55.2708, 25.2048] },
  { name: 'Tel Aviv', region: 'Middle East & Africa', coords: [34.7818, 32.0853] },
  { name: 'Johannesburg', region: 'Middle East & Africa', coords: [28.0473, -26.2041] },
];

const regions = [
  {
    name: 'North America',
    locations: [
      'Los Angeles, CA',
      'San Francisco, CA', 
      'Seattle, WA',
      'Dallas, TX',
      'Chicago, IL',
      'Atlanta, GA',
      'Miami, FL',
      'New York, NY',
      'Toronto, Canada',
      'Vancouver, Canada',
    ],
  },
  {
    name: 'Europe',
    locations: [
      'London, UK',
      'Amsterdam, Netherlands',
      'Frankfurt, Germany',
      'Paris, France',
      'Stockholm, Sweden',
      'Madrid, Spain',
      'Milan, Italy',
      'Warsaw, Poland',
    ],
  },
  {
    name: 'Asia Pacific',
    locations: [
      'Tokyo, Japan',
      'Seoul, South Korea',
      'Singapore',
      'Hong Kong',
      'Sydney, Australia',
      'Melbourne, Australia',
      'Mumbai, India',
      'Bangkok, Thailand',
    ],
  },
  {
    name: 'South America',
    locations: [
      'São Paulo, Brazil',
      'Buenos Aires, Argentina',
      'Santiago, Chile',
    ],
  },
  {
    name: 'Middle East & Africa',
    locations: [
      'Dubai, UAE',
      'Tel Aviv, Israel',
      'Johannesburg, South Africa',
    ],
  },
];

export function Servers() {
  const mapContainer = useRef<HTMLDivElement>(null);
  const map = useRef<maplibregl.Map | null>(null);
  const totalLocations = regions.reduce((acc, region) => acc + region.locations.length, 0);

  useEffect(() => {
    if (!mapContainer.current || map.current) return;

    map.current = new maplibregl.Map({
      container: mapContainer.current,
      style: {
        version: 8,
        sources: {
          'carto-dark': {
            type: 'raster',
            tiles: [
              'https://a.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}@2x.png',
              'https://b.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}@2x.png',
              'https://c.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}@2x.png',
            ],
            tileSize: 256,
            attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors &copy; <a href="https://carto.com/attributions">CARTO</a>',
          },
        },
        layers: [
          {
            id: 'carto-dark-layer',
            type: 'raster',
            source: 'carto-dark',
            minzoom: 0,
            maxzoom: 19,
          },
        ],
      },
      center: [0, 20],
      zoom: 1.5,
      attributionControl: false,
    });

    map.current.addControl(new maplibregl.NavigationControl(), 'top-right');

    serverLocations.forEach((location) => {
      const el = document.createElement('div');
      el.className = 'server-marker';
      el.style.cssText = `
        width: 12px;
        height: 12px;
        background: #22d3ee;
        border-radius: 50%;
        border: 2px solid #0a0a0a;
        box-shadow: 0 0 10px #22d3ee, 0 0 20px rgba(34, 211, 238, 0.5);
        cursor: pointer;
      `;

      new maplibregl.Marker({ element: el })
        .setLngLat(location.coords as [number, number])
        .setPopup(
          new maplibregl.Popup({ offset: 25, closeButton: false }).setHTML(
            `<div style="color: #fff; font-weight: 600; font-size: 14px;">${location.name}</div>
             <div style="color: #9ca3af; font-size: 12px;">${location.region}</div>`
          )
        )
        .addTo(map.current!);
    });

    return () => {
      map.current?.remove();
      map.current = null;
    };
  }, []);

  return (
    <div className="min-h-screen pt-24 pb-20 px-4">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="text-center mb-16">
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-4xl md:text-5xl font-bold text-white mb-4"
          >
            {totalLocations} Server Locations
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="text-gray-400 text-lg max-w-2xl mx-auto"
          >
            Stream from anywhere with ultra-low latency. 
            Our global network ensures your viewers get the best experience.
          </motion.p>
        </div>

        {/* Interactive Map */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.15 }}
          className="mb-16 rounded-2xl overflow-hidden border border-gray-800"
        >
          <div
            ref={mapContainer}
            className="w-full h-[400px] md:h-[500px]"
            style={{ background: '#0a0a0a' }}
          />
        </motion.div>

        {/* Stats */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="grid grid-cols-3 gap-8 max-w-3xl mx-auto mb-20"
        >
          <div className="text-center">
            <div className="text-4xl font-bold text-cyan-400 mb-2">{totalLocations}</div>
            <div className="text-gray-400 text-sm">Locations</div>
          </div>
          <div className="text-center">
            <div className="text-4xl font-bold text-cyan-400 mb-2">5</div>
            <div className="text-gray-400 text-sm">Continents</div>
          </div>
          <div className="text-center">
            <div className="text-4xl font-bold text-cyan-400 mb-2">&lt;50ms</div>
            <div className="text-gray-400 text-sm">Avg Latency</div>
          </div>
        </motion.div>

        {/* Regions Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
          {regions.map((region, index) => (
            <motion.div
              key={region.name}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 * index }}
              className="bg-gray-900/50 border border-gray-800 rounded-2xl p-6"
            >
              <h3 className="text-xl font-semibold text-white mb-4 flex items-center">
                <MapPin className="h-5 w-5 text-cyan-400 mr-2" />
                {region.name}
              </h3>
              <ul className="space-y-2">
                {region.locations.map((location) => (
                  <li
                    key={location}
                    className="text-gray-400 text-sm flex items-center"
                  >
                    <span className="w-1.5 h-1.5 bg-cyan-400 rounded-full mr-3" />
                    {location}
                  </li>
                ))}
              </ul>
            </motion.div>
          ))}
        </div>

        {/* Network Info */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="mt-20 bg-gray-900/50 border border-gray-800 rounded-2xl p-8 md:p-12"
        >
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="text-2xl md:text-3xl font-bold text-white mb-4">
              Enterprise-grade infrastructure
            </h2>
            <p className="text-gray-400 mb-8 leading-relaxed">
              All our servers feature unlimited bandwidth, redundant networking, and 
              enterprise SSD storage. We partner with top-tier data centers to ensure 
              99.9% uptime for your streams.
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <span className="px-4 py-2 bg-gray-800 rounded-full text-sm text-gray-300">
                Unlimited Bandwidth
              </span>
              <span className="px-4 py-2 bg-gray-800 rounded-full text-sm text-gray-300">
                DDoS Protection
              </span>
              <span className="px-4 py-2 bg-gray-800 rounded-full text-sm text-gray-300">
                99.9% Uptime SLA
              </span>
              <span className="px-4 py-2 bg-gray-800 rounded-full text-sm text-gray-300">
                NVMe Storage
              </span>
            </div>
          </div>
        </motion.div>

        {/* CTA */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="mt-16 text-center"
        >
          <Link to="/pricing">
            <Button
              size="lg"
              className="bg-cyan-500 hover:bg-cyan-400 text-black font-semibold px-8"
            >
              Get Started
              <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
          </Link>
        </motion.div>
      </div>
    </div>
  );
}
